import { ActiveAutotraderListing, FetchByVinData, VehicleAttribute, VehiclePossibility } from '../interfaces/interface.vehicle';
import { jsonIf } from '../utils/utils.network';
import { isValidVin } from '../utils/utils.vin';

const extractImageUrl = (rawResponse): string => {
  try {
    const resp = rawResponse?.data;
    return resp.url;
  } catch (err) {
    console.error('Error extractImageUrl', err);
  }
};

export const fetchVehicleImage = (trimID: number) => {
  return fetch(`${process.env.EDGE_URL}/${process.env.V_PREFIX}/vehicles/images?trimId=${trimID}&api_key=${process.env.API_KEY}`, {
    headers: {
      authorization: `Basic ${process.env.ICO_TOKEN}`,
      Date: new Date().toUTCString(),
    },
    method: 'GET',
    mode: 'cors',
    redirect: 'follow',
  }).then(async (res) => {
    if (res.status === 200) {
      return extractImageUrl(await jsonIf(res));
    }
  });
};

export const fetchVehiclePossibilities = async (vin: string): Promise<FetchByVinData> => {
  if (!vin) {
    throw 'VIN is required';
  }

  if (!isValidVin(vin)) {
    throw 'VIN is not valid';
  }

  const res = await fetch(
    `${process.env.EDGE_URL}/${process.env.V_PREFIX}/vehicles/vin/${vin}/?cadsBypass=false&vinVerified=true&fromAuction=undefined&api_key=${process.env.API_KEY}`,
    {
      headers: {
        Authorization: `Basic ${process.env.ICO_TOKEN}`,
        QuestionConversion: 'fury',
        Date: new Date().toUTCString(),
      },
      method: 'GET',
      mode: 'cors',
      redirect: 'follow',
    }
  );

  if (res.status === 200) {
    const jsonData = await res.json();
    const retVal = extractFetchByVinData(jsonData);

    return retVal;
  } else if (res.status === 400) {
    const data = await res.json();
    const returnCode = data?.meta?.codes[0];
    throw `Error getting data for vin ${vin} return code ${returnCode}`;
  } else {
    throw `Error getting data for vin ${vin} response status ${res.status}`;
  }
};

export const fetchActiveAutotraderListing = async (vin: string, svocId: string = ''): Promise<ActiveAutotraderListing> => {
  if (!vin) {
    throw 'VIN is required';
  }

  if (!isValidVin(vin)) {
    throw 'VIN is not valid';
  }

  const res = await fetch(`${process.env.EDGE_URL}/${process.env.V_PREFIX}/vin/activeautotraderlisting/${vin}/?svocId=${svocId}&a=b&api_key=${process.env.API_KEY}`, {
    headers: {
      Authorization: `Basic ${process.env.ICO_TOKEN}`,
      QuestionConversion: 'fury',
      Date: new Date().toUTCString(),
    },
    method: 'GET',
    mode: 'cors',
    redirect: 'follow',
  });

  if (res.ok) {
    const jsonData = await res.json();

    const retVal = extractActiveAutotraderListing(jsonData);
    return retVal;
  }
  throw `Error getting data for vin ${vin} response status ${res.status}`;
};

const extractVehiclePossibilities = (rawResponse) => {
  try {
    const possibleVehicles = rawResponse?.data?.possibilities;
    if (!possibleVehicles || possibleVehicles.length === 0) {
      throw 'Expected at least one possible vehicle';
    } else if (possibleVehicles.length >= 1) {
      const possibilities: VehiclePossibility[] = [];
      for (let i = 0; i < possibleVehicles.length; i++) {
        const vehicle = possibleVehicles[i];
        possibilities.push({
          year: { displayName: vehicle.year.displayName, id: vehicle.year.yearId },
          make: { displayName: vehicle.make.displayName, id: vehicle.make.makeId },
          model: { displayName: vehicle.model.displayName, id: vehicle.model.modelId },
          trim: { displayName: vehicle.trim.displayName, id: vehicle.trim.trimId },
          engines: getAttributeValues(vehicle.engines, vehicle.enginesVinVerified, 'engineId'),
          transmissions: getAttributeValues(vehicle.transmissions, vehicle.transmissionsVinVerified, 'transmissionId'),
          drivetrains: getAttributeValues(vehicle.drivetrains, vehicle.drivetrainsVinVerified, 'drivetrainId'),
          colors: getAttributeValues(vehicle.colors, vehicle.colorsVinVerified, 'optionId'),
          options: vehicle.options ?? [],
          genericOptions: vehicle.genericOptions ?? [],
          enginesVinVerified: vehicle.enginesVinVerified,
          drivetrainsVinVerified: vehicle.drivetrainsVinVerified,
          transmissionsVinVerified: vehicle.transmissionsVinVerified,
          colorsVinVerified: vehicle.colorsVinVerified,
          optionsVinVerified: vehicle.optionsVinVerified,
          cadsUsed: vehicle.cadsUsed,
        });
      }
      console.log('extractVehiclePossibilities', possibilities);
      return possibilities;
    }
  } catch (err) {
    console.error('Error extractVehicleDetails', err);
  }
};

const extractFetchByVinData = (rawResponse) => {
  const fetchByVin: FetchByVinData = {
    possibilities: extractVehiclePossibilities(rawResponse),
    isAutoCheckEligible: rawResponse?.data?.autoCheckData?.isAutoCheckEligible,
    autoCheckData: rawResponse?.data?.autoCheckData,
  };
  return fetchByVin;
};

const extractActiveAutotraderListing = (rawResponse) => {
  const activeAutotraderListing: ActiveAutotraderListing = {
    totalResultCount: rawResponse?.data?.totalResultCount,
    privateSeller: rawResponse?.data?.privateSeller,
    autoListingUrl: rawResponse?.data?.autoListingUrl,
  };
  return activeAutotraderListing;
};

// If attributeVerified is true and isSelected is true, then use that value
// For a vehicle, if the following field is true for the field and IsSelcted == true for the record, it means that value should be used.
// I believe this comes from build data, so not all vehicle or makes will have this set.
// But if it set and IsSelcted == true for a single record, then use that value.
// 1N4AL3APXFC122919 is an example vehicle that has data like this
// TODO: we're mapping these values and remapping in the actions layer.  may need to look at how this fetches and remove the double mapping
const getAttributeValues = (attributeList, attributeVerified, idField): VehicleAttribute[] => {
  let useSelected = false;
  const retVal = [];
  if (attributeVerified) {
    const selectedAttr = attributeList.filter((rec) => rec.isSelected);
    if (selectedAttr.length === 1) {
      useSelected = true;
      retVal.push({
        displayName: selectedAttr[0].displayName,
        id: selectedAttr[0][idField],
        isDefault: selectedAttr[0].isDefault,
        hasOptionRelationships: selectedAttr[0].hasOptionRelationships,
        isSelected: selectedAttr[0].isSelected,
        isDisabled: selectedAttr[0].isDisabled,
      });
    }
  }

  if (!useSelected) {
    attributeList.forEach((attribute) => {
      if (!attributeList.some((rec) => rec['id'] === attribute[idField])) {
        retVal.push({
          displayName: attribute.displayName,
          id: attribute[idField],
          isDefault: attribute.isDefault,
          hasOptionRelationships: attribute.hasOptionRelationships,
          isSelected: attribute.isSelected,
          isDisabled: attribute.isDisabled,
        });
      }
    });
  }
  return retVal;
};

export const fetchVin = async (plateNumber, plateState) => {
  const url = `${process.env.EDGE_URL}/${process.env.V_PREFIX}/plate2vin/lookup?stateCode=${plateState}&plateNumber=${plateNumber}&api_key=${process.env.API_KEY}`;
  const res = await fetch(url, {
    headers: {
      Authorization: `Basic ${process.env.ICO_TOKEN}`,
      QuestionConversion: 'fury',
      Date: new Date().toUTCString(),
    },
    method: 'GET',
    mode: 'cors',
    redirect: 'follow',
  });

  const result = await res.json();
  return result?.data?.vins.map((vinObj) => vinObj.vin); // This returns an array, does it sometimes return multiple?
};

export const fetchVehicle = (yearId, makeId, modelId, trimId) => {
  return fetch(`${process.env.EDGE_URL}/${process.env.V_PREFIX}/vehicles/trims?a=b&api_key=${process.env.API_KEY}&yearId=${yearId}&makeId=${makeId}&modelId=${modelId}`, {
    headers: {
      Authorization: `Basic ${process.env.ICO_TOKEN}`,
    },
    method: 'GET',
    mode: 'cors',
    redirect: 'follow',
  })
    .then((res) => jsonIf(res))
    .then((res) => {
      return res.data.find((t) => t.trimId === trimId);
    });
};

export const fetchColors = async (trimId) => {
  return await fetch(`${process.env.EDGE_URL}/${process.env.V_PREFIX}/vehicles/colors?a=b&api_key=${process.env.API_KEY}&trimId=${trimId}`, {
    headers: {
      Authorization: `Basic ${process.env.ICO_TOKEN}`,
    },
    method: 'GET',
    mode: 'cors',
    redirect: 'follow',
  })
    .then((res) => jsonIf(res))
    .then((res) => {
      return res.data;
    });
};

export const fetchEngines = (trimId) => {
  return fetch(`${process.env.EDGE_URL}/${process.env.V_PREFIX}/vehicles/engines?a=b&api_key=${process.env.API_KEY}&trimId=${trimId}`, {
    headers: {
      Authorization: `Basic ${process.env.ICO_TOKEN}`,
    },
    method: 'GET',
    mode: 'cors',
    redirect: 'follow',
  })
    .then((res) => jsonIf(res))
    .then((res) => {
      return res.data;
    });
};

export const fetchDrivetrains = (trimId) => {
  return fetch(`${process.env.EDGE_URL}/${process.env.V_PREFIX}/vehicles/drivetrains?a=b&api_key=${process.env.API_KEY}&trimId=${trimId}`, {
    headers: {
      Authorization: `Basic ${process.env.ICO_TOKEN}`,
    },
    method: 'GET',
    mode: 'cors',
    redirect: 'follow',
  })
    .then((res) => jsonIf(res))
    .then((res) => {
      return res.data;
    });
};

export const fetchTransmissions = (trimId) => {
  return fetch(`${process.env.EDGE_URL}/${process.env.V_PREFIX}/vehicles/transmissions?a=b&api_key=${process.env.API_KEY}&trimId=${trimId}`, {
    headers: {
      Authorization: `Basic ${process.env.ICO_TOKEN}`,
    },
    method: 'GET',
    mode: 'cors',
    redirect: 'follow',
  })
    .then((res) => jsonIf(res))
    .then((res) => {
      return res.data;
    });
};

export const fetchOptions = (trimId) => {
  return fetch(`${process.env.EDGE_URL}/${process.env.V_PREFIX}/vehicles/options?a=b&api_key=${process.env.API_KEY}&trimId=${trimId}`, {
    headers: {
      Authorization: `Basic ${process.env.ICO_TOKEN}`,
    },
    method: 'GET',
    mode: 'cors',
    redirect: 'follow',
  })
    .then((res) => jsonIf(res))
    .then((res) => {
      return res.data;
    });
};

export const fetchOptionsChanges = (trimId, selectedOptions) => {
  return fetch(`${process.env.EDGE_URL}/${process.env.V_PREFIX}/vehicles/options/lastchanged/?trimId=${trimId}&optionsState=${selectedOptions}&api_key=${process.env.API_KEY}`, {
    headers: {
      Authorization: `Basic ${process.env.ICO_TOKEN}`,
    },
    method: 'GET',
    mode: 'cors',
    redirect: 'follow',
  })
    .then((res) => jsonIf(res))
    .then((res) => {
      return res.data;
    });
};

export const fetchVoiVinData = (voiVin: string) => {
  return fetch(`${process.env.EDGE_URL}/${process.env.V_PREFIX}/vin/replacementvehicle/vindecode/${voiVin}?a=b&api_key=${process.env.API_KEY}`, {
    headers: {
      authorization: `Basic ${process.env.ICO_TOKEN}`,
      Date: new Date().toUTCString(),
    },
    method: 'GET',
    mode: 'cors',
    redirect: 'follow',
  })
    .then((res) => jsonIf(res))
    .then((res) => {
      return res.data;
    });
};

export const fetchVehicleTypicalMileage = async (trimId: number): Promise<number> => {
  if (!trimId) {
    throw 'trimId is required';
  }

  const url = `${process.env.EDGE_URL}/${process.env.V_PREFIX}/vehicles/typicalmileage`;
  const parms = new URLSearchParams({
    trimId: trimId.toString(),
    a: 'b',
    api_key: process.env.API_KEY,
  });

  const resp = await fetch(`${url}?${parms}`, {
    headers: {
      Authorization: `Basic ${process.env.ICO_TOKEN}`,
      'Content-Type': 'text/json',
    },
    method: 'GET',
    mode: 'cors',
    redirect: 'follow',
  });

  if (resp.ok) {
    const result = await resp.json();
    return result.data?.typicalMileage;
  }
  throw `Error getting typical mileage for trimId ${trimId} response status ${resp.status}`;
};

export default {
  fetchVehicleImage,
  fetchVin,
  fetchVehiclePossibilities,
  fetchActiveAutotraderListing,
  fetchVehicle,
  fetchColors,
  fetchEngines,
  fetchDrivetrains,
  fetchTransmissions,
  fetchOptions,
  fetchOptionsChanges,
  fetchVoiVinData,
  fetchVehicleTypicalMileage,
};
